<template>
  <div class="jim-scheduling-container freight-page">

    <!-- Header row -->
    <div class="page-header-row">
      <div class="page-header-left"></div>
      <h1 class="page-heading">FREIGHT ANALYSIS</h1>
      <div class="page-header-right"></div>
    </div>

    <div class="main-content">

      <!-- LEFT SIDE CONTROLS -->
      <div class="left-side-section">

        <div class="control" style="width:100%;">
          <label>Start Date</label>
          <input v-model="startDate" type="date" />
        </div>

        <div class="control" style="width:100%;">
          <label>End Date</label>
          <input v-model="endDate" type="date" />
        </div>

        <div class="control" style="width:100%;">
          <label>Freight Search End</label>
          <input v-model="freightEndDate" type="date" />
        </div>

        <div class="control" style="width:100%;">
            <label>Journal</label>
            <input v-model="journal" type="text" />
        </div>

        <div class="control" style="width:100%;">
          <label>CU</label>
          <select v-model="cu" class="select-input">
            <option value="ALL">ALL</option>
            <option value="00">00</option>
            <option value="01">01</option>
            <option value="02">02</option>
            <option value="03">03</option>
            <option value="04">04</option>
            <option value="05">05</option>
            <option value="09">09</option>
          </select>
        </div>

        <div class="control" style="width:100%;">
          <label>Freight Account</label>
          <input v-model="acct" type="text" />
        </div>

        <div class="control" style="width:100%;">
          <label>Status Filter</label>
          <select v-model="statusFilter" class="select-input">
            <option value="ALL">ALL</option>
            <option value="OK_SAME_PERIOD">Same Period</option>
            <option value="FREIGHT_KEYED_LATE">Keyed Late</option>
            <option value="FREIGHT_KEYED_EARLY">Keyed Early</option>
            <option value="ORDER_NOT_FOUND">Order Not Found</option>
            <option value="NO_ORDER_NUMBER_IN_DESCRIPTION">No Order #</option>
            <option value="NO_SHIP_DATE">No Ship Date</option>
          </select>
        </div>

        <div class="control" style="width:100%;">
          <label>Options</label>
          <div class="toggle-row">
            <label class="toggle">
              <input type="checkbox" v-model="showOnlyProblems" />
              <span>Problems Only</span>
            </label>
            <label class="toggle">
              <input type="checkbox" v-model="includeMissingAccrual" />
              <span>Missing Accrual</span>
            </label>
            <label class="toggle">
              <input type="checkbox" v-model="includeV" />
              <span>Include V</span>
            </label>
          </div>
        </div>

        <button class="plan-it-btn" :disabled="loading || !canRun" @click="runAnalysis">
          {{ loading ? "Loading..." : "Run Analysis" }}
        </button>

        <button class="plan-it-btn"
                :disabled="accrualLoading || !canRun"
                @click="runAccrualByPeriod">
          {{ accrualLoading ? "Loading..." : "Run Accrual" }}
        </button>

        <button class="plan-it-btn" :disabled="!analysisLoaded" @click="exportCSV">
          Export CSV
        </button>

        <button class="plan-it-btn" :disabled="!accrualLoaded" @click="exportAccrualCSV">
          Export Accrual
        </button>

        <button class="plan-it-btn" :disabled="!analysisLoaded" @click="printFreight">
          Print
        </button>

        <div v-if="error" class="panel error-box" style="margin-top:10px;">
          {{ error }}
        </div>

        <div v-if="analysisLoaded && problemCount > 0"
             class="panel warn-box"
             style="margin-top:10px;">
          <div class="warn-title">Freight Exceptions</div>
          <div class="warn-body">
            <div class="big-number">{{ problemCount }}</div>
            <div class="small-muted">
              Rows need review based on date mismatch, missing order number, missing order history, or missing ship date.
            </div>
          </div>
        </div>

      </div>

      <!-- RIGHT SIDE CONTENT -->
      <div class="right-side-section">

        <!-- Summary bar -->
        <div v-if="analysisLoaded" class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">GL Window</div>
            <div class="summary-value">{{ analysis.startDate }} → {{ analysis.endDate }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">CU</div>
            <div class="summary-value">{{ analysis.cu }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">GL Lines</div>
            <div class="summary-value">{{ summary.total_gl_lines || 0 }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Expanded</div>
            <div class="summary-value">{{ summary.total_expanded_rows || 0 }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Late</div>
            <div class="summary-value">{{ summary.keyed_late_rows || 0 }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Missing</div>
            <div class="summary-value">{{ missingSummary.orders_missing_freight || 0 }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">JPL Truck</div>
            <div class="summary-value">{{ summary.jpl_truck_rows || 0 }}</div>
        </div>
        </div>

        <!-- Exception tiles -->
        <div v-if="analysisLoaded" class="tile-row">
          <div class="metric-tile">
            <div class="metric-label">Same Period</div>
            <div class="metric-value">{{ summary.same_period_rows || 0 }}</div>
          </div>

          <div class="metric-tile dangerish">
            <div class="metric-label">Keyed Late</div>
            <div class="metric-value">{{ summary.keyed_late_rows || 0 }}</div>
          </div>

          <div class="metric-tile warningish">
            <div class="metric-label">Keyed Early</div>
            <div class="metric-value">{{ summary.keyed_early_rows || 0 }}</div>
          </div>

          <div class="metric-tile dangerish">
            <div class="metric-label">Order Not Found</div>
            <div class="metric-value">{{ summary.order_not_found_rows || 0 }}</div>
          </div>

          <div class="metric-tile warningish">
            <div class="metric-label">No Order #</div>
            <div class="metric-value">{{ summary.no_order_number_lines || 0 }}</div>
          </div>

          <div class="metric-tile warningish">
            <div class="metric-label">No Ship Date</div>
            <div class="metric-value">{{ summary.no_ship_date_rows || 0 }}</div>
          </div>
        </div>

        <!-- Accrual By Period -->
        <div v-if="accrualLoaded" class="panel report-panel accrual-panel">
        <div class="panel-title-row">
            <div>
            <div class="panel-title">Freight Accrual / Reclass By Period</div>
            <div class="small-muted">
                Correct period is based on OE ship date. AP freight is pulled from journal {{ accrualData.journal || journal }}.
            </div>
            </div>

            <div class="accrual-total-box">
            <div class="small-muted">Net Adjustment Total</div>
            <div class="accrual-total">{{ formatMoney(accrualData.net_adjustment_total) }}</div>
            </div>
        </div>

        <div class="tile-row accrual-tile-row">
            <div class="metric-tile">
            <div class="metric-label">Shipped Orders</div>
            <div class="metric-value">{{ accrualData.shipped_orders_checked || 0 }}</div>
            </div>

            <div class="metric-tile">
            <div class="metric-label">Missing Freight</div>
            <div class="metric-value">{{ accrualData.missing_freight_orders || 0 }}</div>
            </div>

            <div class="metric-tile warningish">
            <div class="metric-label">Missing Estimate</div>
            <div class="metric-value">{{ accrualData.missing_without_estimate || 0 }}</div>
            </div>

            <div class="metric-tile dangerish">
            <div class="metric-label">J Truck Exceptions</div>
            <div class="metric-value">{{ accrualData.jpl_truck_freight_exception_count || 0 }}</div>
            </div>

            <div class="metric-tile">
            <div class="metric-label">Missing Est. Total</div>
            <div class="metric-value money-small">{{ formatMoney(accrualData.missing_freight_estimate_total) }}</div>
            </div>

            <div class="metric-tile">
            <div class="metric-label">Movements</div>
            <div class="metric-value">{{ accrualData.movement_count || 0 }}</div>
            </div>
        </div>

        <div class="table-scroll">
            <table class="freight-table accrual-table">
            <thead>
                <tr>
                <th class="sticky-col order-col">Period</th>
                <th style="text-align:right;">Late Add</th>
                <th style="text-align:right;">Late Offset</th>
                <th style="text-align:right;">Early Remove</th>
                <th style="text-align:right;">Early Add</th>
                <th style="text-align:right;">Missing Freight</th>
                <th style="text-align:right;">Net Adjustment</th>
                <th style="text-align:right;">Rows</th>
                </tr>
            </thead>

            <tbody>
                <tr v-for="p in accrualPeriods"
                    :key="p.period_start"
                    :class="{ 'current-period-row': p.period_start === monthStart(startDate) }"
                    @dblclick="openAccrualDetail(p)">
                <td class="sticky-col order-col clickable-cell" @click="openAccrualDetail(p)">
                    {{ formatMonthLabelShort(p.period_start) }}
                </td>

                <td style="text-align:right;">{{ formatMoney(p.keyed_late_add_to_ship_period) }}</td>
                <td style="text-align:right;">{{ formatMoney(p.keyed_late_offset_gl_period) }}</td>
                <td style="text-align:right;">{{ formatMoney(p.keyed_early_remove_from_gl_period) }}</td>
                <td style="text-align:right;">{{ formatMoney(p.keyed_early_add_to_ship_period) }}</td>
                <td style="text-align:right;">{{ formatMoney(p.missing_freight_estimate) }}</td>
                <td style="text-align:right; font-weight:bold;">{{ formatMoney(p.net_adjustment) }}</td>
                <td style="text-align:right;">{{ p.movement_count || 0 }}</td>
                </tr>

                <tr v-if="accrualPeriods.length === 0">
                <td colspan="8" class="empty-row">
                    No freight accrual/reclass movements found.
                </td>
                </tr>
            </tbody>
            </table>
        </div>

        <div class="small-muted" style="margin-top:10px;">
            Positive net adjustment increases freight expense in that period. Negative net adjustment offsets/reverses freight expense in that period.
            Double-click a period to see order-level support.
        </div>
        </div>

        <div v-if="accrualError" class="panel error-box" style="margin-bottom:12px;">
        {{ accrualError }}
        </div>

        

        <!-- Main Freight Table -->
        <div v-if="analysisLoaded" class="sheet-scroll-container">
          <div class="sheet-wrapper">

            <div class="panel report-panel">
              <div class="panel-title-row">
                <div>
                  <div class="panel-title">Freight GL vs Shipped Orders</div>
                  <div class="small-muted">
                    One row per freight GL line per parsed order number.
                  </div>
                </div>

                <div class="small-muted">
                  Showing {{ filteredRows.length }} of {{ analysisRows.length }}
                </div>
              </div>

              <div class="table-scroll">
                <table class="freight-table">
                  <thead>
                    <tr>
                      <th class="sticky-col order-col">Order</th>
                      <th>Status</th>
                      <th>OE Match</th>
                      <th>GL Date</th>
                      <th>Ship Date</th>
                      <th>CU</th>
                      <th>Vendor</th>
                      <th>Ship Via</th>
                      <th>MFG Loc</th>
                      <th style="text-align:right;">Cubes</th>
                      <th style="text-align:right;">$/Cube</th>
                      <th style="text-align:right;">Est. Freight</th>
                      <th style="text-align:right;">Actual vs Est.</th>
                      <th>Description</th>
                      <th>Invoice / Doc</th>                      
                      <th style="text-align:right;">Freight</th>
                      <th style="text-align:right;">Allocated</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="row in filteredRows"
                        :key="rowKey(row)"
                        :class="statusRowClass(row)"
                        @dblclick="openDetail(row)">
                      <td class="sticky-col order-col clickable-cell" @click="openDetail(row)">
                        {{ row.parsed_order_no || "-" }}
                      </td>

                      <td>
                        <span class="status-chip" :class="statusChipClass(row.period_status)">
                            {{ statusLabel(row.period_status) }}
                        </span>
                        </td>

                        <td>
                        <span v-if="row.order_history_ambiguous" class="status-chip chip-warn">
                            Review {{ row.order_history_count || "" }}
                        </span>
                        <span v-else-if="Number(row.order_history_count || 0) > 1" class="status-chip chip-ok">
                            Matched {{ row.order_history_count }}
                        </span>
                        <span v-else>
                            {{ row.order_history_match_method || "" }}
                        </span>
                     </td>

                        <td>{{ fmtDate(row.gl_date || row.gl_date_yyyy_mm_dd) }}</td>
                      <td>{{ fmtDate(row.ship_date || row.ship_date_yyyy_mm_dd) }}</td>
                      <td>{{ row.cu || "" }}</td>
                      <td>{{ row.vendor_name || row.vendor_no || "" }}</td>
                      <td>
                        <span v-if="row.is_jpl_truck" class="status-chip chip-warn">J - JPL Truck</span>
                        <span v-else>{{ row.ship_via_cd || "" }}</span>
                      </td>
                      <td>{{ row.mfg_loc || "" }}</td>
                      <td style="text-align:right;">{{ formatNumber(row.cubes) }}</td>
                      <td style="text-align:right;">{{ formatMoney(row.freight_per_cube) }}</td>
                      <td style="text-align:right;">{{ formatMoney(row.estimated_freight) }}</td>
                      <td style="text-align:right;">{{ formatMoney(row.freight_vs_estimate) }}</td>
                      <td class="desc-cell">{{ row.description || "" }}</td>
                      <td>{{ row.faktuurnr || row.docnumber || row.bkstnr || "" }}</td>
                      <td style="text-align:right;">{{ formatMoney(row.freight_amount) }}</td>
                      <td style="text-align:right;">{{ formatMoney(row.allocated_amount) }}</td>
                    </tr>

                    <tr v-if="filteredRows.length === 0">
                      <td colspan="10" class="empty-row">
                        No freight rows match the current filters.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="small-muted" style="margin-top:10px;">
                Double-click a row, or click the order number, to inspect the freight GL line and matched order history.
              </div>
            </div>

            <!-- Missing Accrual Panel -->
            <div v-if="includeMissingAccrual && missingLoaded" class="panel report-panel missing-panel">
              <div class="panel-title-row">
                <div>
                  <div class="panel-title">Missing Freight Accrual</div>
                  <div class="small-muted">
                    Shipped orders in period with no parsed freight GL match through {{ freightEndDate }}.
                  </div>
                </div>

                <div class="small-muted">
                  Missing {{ missingRows.length }}
                </div>

                <div class="small-muted">
                    Estimated Accrual:
                    <b>{{ formatMoney(missingSummary.estimated_accrual_total) }}</b>
                    • Missing Estimate:
                    <b>{{ missingSummary.missing_without_estimate || 0 }}</b>
                </div>
              </div>

              <div class="table-scroll">
                <table class="freight-table mini-wide">
                  <thead>
                    <tr>
                      <th class="sticky-col order-col">Order</th>
                      <th>Ship Date</th>
                      <th>Invoice</th>
                      <th>Customer</th>
                      <th>Ship To</th>
                      <th>MFG Loc</th>
                      <th>Ship Via</th>
                      <th style="text-align:right;">Cubes</th>
                      <th style="text-align:right;">$/Cube</th>
                      <th style="text-align:right;">Est. Freight</th>
                      <th style="text-align:right;">Sales Amount</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="row in missingRows" :key="`MISSING_${row.ord_no}`">
                      <td class="sticky-col order-col">{{ row.ord_no }}</td>
                      <td>{{ fmtDate(row.ship_date || row.ship_date_yyyy_mm_dd) }}</td>
                      <td>{{ row.inv_no || "" }}</td>
                      <td>{{ row.bill_to_name || row.customer_no || row.customer_no_alt || "" }}</td>
                      <td>{{ row.ship_to_name || row.ship_to_no || "" }}</td>
                      <td>{{ row.mfg_loc || "" }}</td>
                      <td>{{ row.ship_via_cd_best || "" }}</td>
                      <td style="text-align:right;">{{ formatNumber(row.cubes) }}</td>
                      <td style="text-align:right;">{{ formatMoney(row.freight_per_cube) }}</td>
                      <td style="text-align:right;">{{ formatMoney(row.estimated_freight) }}</td>
                      <td style="text-align:right;">{{ formatMoney(row.tot_sls_amt || row.sls_amt) }}</td>
                    </tr>

                    <tr v-if="missingRows.length === 0">
                      <td colspan="6" class="empty-row">
                        No missing freight accrual rows found.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>

        <div v-else class="panel hint-panel">
          Select a date range and CU, then click <b>Run Analysis</b>.
        </div>

      </div>
    </div>

    <!-- Accrual Detail Modal -->
<div v-if="accrualDetailOpen" class="modal-overlay" @click.self="closeAccrualDetail">
  <div class="modal panel">
    <div class="modal-header">
      <div class="modal-title">
        Accrual Support: {{ formatMonthLabelShort(accrualDetailPeriod?.period_start) }}
      </div>
      <button class="modal-close" @click="closeAccrualDetail">✕</button>
    </div>

    <div class="small-muted" style="margin-top:6px;">
      Net Adjustment:
      <b>{{ formatMoney(accrualDetailPeriod?.net_adjustment) }}</b>
      • Rows:
      <b>{{ accrualDetailRows.length }}</b>
    </div>

    <div class="table-scroll" style="margin-top:12px;">
      <table class="mini-table accrual-detail-table">
        <thead>
          <tr>
            <th>Movement</th>
            <th>Order</th>
            <th>Ship Date</th>
            <th>GL Date</th>
            <th>Vendor</th>
            <th>Invoice/Doc</th>
            <th>MFG Loc</th>
            <th>Ship Via</th>
            <th style="text-align:right;">Cubes</th>
            <th style="text-align:right;">$/Cube</th>
            <th style="text-align:right;">Est. Freight</th>
            <th style="text-align:right;">Allocated</th>
            <th style="text-align:right;">Amount</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="m in accrualDetailRows"
              :key="`${m.period_start}_${m.movement_type}_${m.ord_no}_${m.gl_id || 'NOGL'}`">
            <td>{{ m.movement_label || m.movement_type }}</td>
            <td>{{ m.ord_no || "" }}</td>
            <td>{{ fmtDate(m.ship_date) }}</td>
            <td>{{ fmtDate(m.gl_date) }}</td>
            <td>{{ m.vendor_name || m.vendor_no || "" }}</td>
            <td>{{ m.invoice_doc || "" }}</td>
            <td>{{ m.mfg_loc || "" }}</td>
            <td>{{ m.ship_via_cd || "" }}</td>
            <td style="text-align:right;">{{ formatNumber(m.cubes) }}</td>
            <td style="text-align:right;">{{ formatMoney(m.freight_per_cube) }}</td>
            <td style="text-align:right;">{{ formatMoney(m.estimated_freight) }}</td>
            <td style="text-align:right;">{{ formatMoney(m.allocated_amount) }}</td>
            <td style="text-align:right; font-weight:bold;">{{ formatMoney(m.amount) }}</td>
          </tr>

          <tr v-if="accrualDetailRows.length === 0">
            <td colspan="13" class="empty-row">
              No support rows for this period.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</div>

    <!-- Detail Modal -->
    <div v-if="detailOpen" class="modal-overlay" @click.self="closeDetail">
      <div class="modal panel">
        <div class="modal-header">
          <div class="modal-title">
            Freight Detail: {{ detailRow?.parsed_order_no || "No Order #" }}
          </div>
          <button class="modal-close" @click="closeDetail">✕</button>
        </div>

        <div class="small-muted" style="margin-top:6px;">
          Status:
          <b>{{ statusLabel(detailRow?.period_status) }}</b>
          • GL Date:
          <b>{{ fmtDate(detailRow?.gl_date || detailRow?.gl_date_yyyy_mm_dd) }}</b>
          • CU:
          <b>{{ detailRow?.cu || "" }}</b>
        </div>

        <div class="modal-grid" style="margin-top:12px;">

          <div class="panel">
            <div class="warn-title">Freight GL Line</div>

            <div class="kv-row">
              <span>Vendor</span>
              <b>{{ detailRow?.vendor_name || detailRow?.vendor_no || "" }}</b>
            </div>

            <div class="kv-row">
              <span>Amount</span>
              <b>{{ formatMoney(detailRow?.freight_amount) }}</b>
            </div>

            <div class="kv-row">
              <span>Allocated</span>
              <b>{{ formatMoney(detailRow?.allocated_amount) }}</b>
            </div>

            <div class="kv-row">
              <span>Order Count</span>
              <b>{{ detailRow?.order_count_on_freight_line || "" }}</b>
            </div>

            <div class="kv-row">
              <span>Invoice / Doc</span>
              <b>{{ detailRow?.faktuurnr || detailRow?.docnumber || detailRow?.bkstnr || "" }}</b>
            </div>

            <div class="kv-row">
              <span>Journal</span>
              <b>{{ detailRow?.journal || "" }}</b>
            </div>

            <div class="kv-block">
              <span>Description</span>
              <p>{{ detailRow?.description || "" }}</p>
            </div>
          </div>

          <div class="panel">
            <div class="warn-title">Order History</div>

            <template v-if="detailRow?.order_found">
              <div class="kv-row">
                <span>Order</span>
                <b>{{ detailRow?.parsed_order_no }}</b>
              </div>

              <div class="kv-row">
                <span>Ship Date</span>
                <b>{{ fmtDate(detailRow?.ship_date || detailRow?.ship_date_yyyy_mm_dd) }}</b>
              </div>

              <div class="kv-row">
                <span>Ship Period</span>
                <b>{{ detailRow?.ship_month_start || "" }}</b>
              </div>

              <div class="kv-row">
                <span>Invoice</span>
                <b>{{ detailRow?.order_history?.inv_no || "" }}</b>
              </div>

              <div class="kv-row">
                <span>OE Match Method</span>
                <b>{{ detailRow?.order_history_match_method || "" }}</b>
              </div>

              <div class="kv-row">
                <span>OE History Rows</span>
                <b>{{ detailRow?.order_history_count || 0 }}</b>
              </div>

              <div v-if="detailRow?.order_history_ambiguous" class="panel warn-box" style="margin-top:8px;">
                Multiple OE history rows exist for this order number. Pablo selected the best candidate, but this should be reviewed.
              </div>

              <div class="kv-row">
                <span>Customer</span>
                <b>
                  {{
                    detailRow?.order_history?.bill_to_name ||
                    detailRow?.order_history?.customer_no ||
                    detailRow?.order_history?.customer_no_alt ||
                    ""
                  }}
                </b>
              </div>

              <div class="kv-row">
                <span>Ship To</span>
                <b>
                  {{
                    detailRow?.order_history?.ship_to_name ||
                    detailRow?.order_history?.ship_to_no ||
                    ""
                  }}
                </b>
              </div>
            </template>

            <template v-else>
              <div class="panel error-box">
                No matching order history was found for this parsed order number.
              </div>
            </template>
          </div>

        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "FreightAnalysisPage",

  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");

    const first = `${yyyy}-${mm}-01`;
    const last = this.lastDayOfMonth(yyyy, Number(mm));

    return {
      startDate: first,
      endDate: last,
      freightEndDate: last,

      cu: "ALL",
      acct: "60600",

      statusFilter: "ALL",
      showOnlyProblems: false,
      includeMissingAccrual: true,
      includeV: false,

      loading: false,
      error: null,

      analysis: null,
      missingAccrual: null,

      detailOpen: false,
      detailRow: null,

      accrualLoading: false,
      accrualError: null,
      accrualData: null,
      accrualDetailOpen: false,
      accrualDetailPeriod: null,
      accrualDetailRows: [],

      journal: "200"
    };
  },

  computed: {
    canRun() {
      return !!this.startDate && !!this.endDate && !!this.freightEndDate && !!this.cu && !!this.acct;
    },

    analysisLoaded() {
      return !!this.analysis;
    },

    missingLoaded() {
      return !!this.missingAccrual;
    },

    summary() {
      return this.analysis?.summary || {};
    },

    missingSummary() {
      return this.missingAccrual?.summary || {};
    },

    analysisRows() {
      return this.analysis?.rows || [];
    },

    missingRows() {
      return this.missingAccrual?.missing || [];
    },

    problemStatuses() {
      return new Set([
        "FREIGHT_KEYED_LATE",
        "FREIGHT_KEYED_EARLY",
        "ORDER_NOT_FOUND",
        "NO_ORDER_NUMBER_IN_DESCRIPTION",
        "NO_SHIP_DATE"
      ]);
    },

    problemCount() {
        return this.analysisRows.filter(r =>
            this.problemStatuses.has(r.period_status) ||
            r.is_jpl_truck ||
            r.order_history_ambiguous
        ).length + (this.includeMissingAccrual ? this.missingRows.length : 0);
    },

    filteredRows() {
      let rows = [...this.analysisRows];

      if (this.statusFilter !== "ALL") {
        rows = rows.filter(r => r.period_status === this.statusFilter);
      }

      if (this.showOnlyProblems) {
        rows = rows.filter(r =>
            this.problemStatuses.has(r.period_status) ||
            r.is_jpl_truck ||
            r.order_history_ambiguous
        );
      }

      return rows;
    },

    accrualLoaded() {
        return !!this.accrualData;
    },

    accrualPeriods() {
        return this.accrualData?.periods || [];
    },

    accrualMovements() {
        return this.accrualData?.movements || [];
    },

    accrualSummary() {
        return this.accrualData || {};
    },

    currentPeriodAccrualRow() {
        const p = this.monthStart(this.startDate);
        return this.accrualPeriods.find(x => x.period_start === p) || null;
    }
  },

  methods: {
    lastDayOfMonth(y, m) {
      const d = new Date(y, m, 0);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    async runAnalysis() {
      if (this.loading || !this.canRun) return;

      this.loading = true;
      this.error = null;
      this.analysis = null;
      this.missingAccrual = null;

      try {
        const baseParams = {
            startDate: this.startDate,
            endDate: this.endDate,
            cu: this.cu,
            acct: this.acct,
            journal: this.journal || "200",
            includeV: this.includeV ? "1" : "0",
            limit: 5000
        };

        const analysisRes = await axios.get("/freightAnalysis", {
            params: baseParams
        });

        this.analysis = analysisRes.data;

        if (this.includeMissingAccrual) {
            const missingRes = await axios.get("/freightMissingAccrual", {
                params: {
                ...baseParams,
                freightStartDate: this.startDate,
                freightEndDate: this.freightEndDate,
                limit: 10000
                }
            });

            this.missingAccrual = missingRes.data;
        }

      } catch (err) {
        console.error("runAnalysis error:", err);
        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load freight analysis.";
      } finally {
        this.loading = false;
      }
    },

    rowKey(row) {
      return [
        row.gl_id,
        row.parsed_order_no || "NO_ORDER",
        row.period_status || "STATUS"
      ].join("_");
    },

    statusLabel(status) {
      if (status === "OK_SAME_PERIOD") return "Same Period";
      if (status === "FREIGHT_KEYED_LATE") return "Keyed Late";
      if (status === "FREIGHT_KEYED_EARLY") return "Keyed Early";
      if (status === "ORDER_NOT_FOUND") return "Order Not Found";
      if (status === "NO_ORDER_NUMBER_IN_DESCRIPTION") return "No Order #";
      if (status === "NO_SHIP_DATE") return "No Ship Date";
      return status || "";
    },

    statusChipClass(status) {
      return {
        "chip-ok": status === "OK_SAME_PERIOD",
        "chip-danger": status === "FREIGHT_KEYED_LATE" || status === "ORDER_NOT_FOUND",
        "chip-warn": status === "FREIGHT_KEYED_EARLY" ||
                     status === "NO_ORDER_NUMBER_IN_DESCRIPTION" ||
                     status === "NO_SHIP_DATE"
      };
    },

    statusRowClass(row) {
      return {
        "row-ok": row.period_status === "OK_SAME_PERIOD",
        "row-danger": row.period_status === "FREIGHT_KEYED_LATE" || row.period_status === "ORDER_NOT_FOUND",
        "row-warn": row.period_status === "FREIGHT_KEYED_EARLY" ||
                    row.period_status === "NO_ORDER_NUMBER_IN_DESCRIPTION" ||
                    row.period_status === "NO_SHIP_DATE"
      };
    },

    formatNumber(v) {
        if (v === null || v === undefined || v === "") return "";
        const n = Number(v);
        if (!isFinite(n)) return "";
        return n.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    },

    formatMoney(v) {
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      if (!isFinite(n)) return "";
      const abs = Math.abs(n);
      const formatted = abs.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return n < 0 ? `(${formatted})` : formatted;
    },

    fmtDate(v) {
      if (!v) return "";
      const d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    },

    openDetail(row) {
      this.detailRow = row;
      this.detailOpen = true;
    },

    closeDetail() {
      this.detailOpen = false;
      this.detailRow = null;
    },

    monthStart(v) {
        if (!v) return null;
        const d = new Date(`${v}T00:00:00`);
        if (isNaN(d.getTime())) return null;

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        return `${yyyy}-${mm}-01`;
    },

    formatMonthLabelShort(iso) {
        if (!iso) return "";
        const d = new Date(`${iso}T00:00:00`);
        if (isNaN(d.getTime())) return String(iso);

        const mo = d.toLocaleString("en-US", { month: "short" });
        const yy = String(d.getFullYear()).slice(-2);
        return `${mo}-${yy}`;
    },

    async runAccrualByPeriod() {
        if (this.accrualLoading || !this.canRun) return;

        this.accrualLoading = true;
        this.accrualError = null;
        this.accrualData = null;

        try {
            const { data } = await axios.get("/freightAccrualByPeriod", {
            params: {
                startDate: this.startDate,
                endDate: this.endDate,
                freightStartDate: this.startDate,
                freightEndDate: this.freightEndDate,
                cu: this.cu,
                acct: this.acct,
                journal: this.journal || "200",
                includeV: this.includeV ? "1" : "0",
                limit: 20000
            }
            });

            this.accrualData = data;
        } catch (err) {
            console.error("runAccrualByPeriod error:", err);
            this.accrualError =
            err?.response?.data?.error ||
            err?.response?.data?.details ||
            "Failed to load freight accrual by period.";
        } finally {
            this.accrualLoading = false;
        }
    },

    runFullFreightClose() {
        this.runAnalysis();
        this.runAccrualByPeriod();
    },

    openAccrualDetail(periodRow) {
        if (!periodRow) return;

        this.accrualDetailPeriod = periodRow;

        this.accrualDetailRows = this.accrualMovements.filter(m =>
            String(m.period_start || "") === String(periodRow.period_start || "")
        );

        this.accrualDetailOpen = true;
    },

    closeAccrualDetail() {
        this.accrualDetailOpen = false;
        this.accrualDetailPeriod = null;
        this.accrualDetailRows = [];
    },

    exportAccrualCSV() {
        if (!this.accrualLoaded) return;

        const rows = [];

        rows.push([
            "Period",
            "Movement Type",
            "Movement Label",
            "Amount",
            "Order",
            "OE Match Method",
            "OE History Count",
            "OE Match Ambiguous",
            "Ship Date",
            "Ship Period",
            "GL Date",
            "GL Period",
            "Vendor",
            "Invoice/Doc",
            "MFG Loc",
            "Ship Via",
            "Cubes",
            "Freight Per Cube",
            "Estimated Freight",
            "Allocated Amount",
            "Description"
        ]);

        for (const m of this.accrualMovements) {
            rows.push([
            m.period_start || "",
            m.movement_type || "",
            m.movement_label || "",
            m.amount ?? "",
            m.ord_no || "",
            m.order_history_match_method || "",
            m.order_history_count ?? "",
            m.order_history_ambiguous ? "YES" : "NO",
            m.ship_date || "",
            m.ship_period || "",
            m.gl_date || "",
            m.gl_period || "",
            m.vendor_name || m.vendor_no || "",
            m.invoice_doc || "",
            m.mfg_loc || "",
            m.ship_via_cd || "",
            m.cubes ?? "",
            m.freight_per_cube ?? "",
            m.estimated_freight ?? "",
            m.allocated_amount ?? "",
            m.description || ""
            ]);
        }

        const csv = rows
            .map(r => r.map(val => `"${String(val ?? "").replace(/"/g, '""')}"`).join(","))
            .join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
            "download",
            `freight_accrual_by_period_${this.cu}_${this.startDate}_${this.endDate}.csv`
        );

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    },

    exportCSV() {
        if (!this.analysisLoaded) return;

        const rows = [];

        rows.push([
            "Type",
            "Status",
            "Order",
            "OE Match Method",
            "OE History Count",
            "OE Match Ambiguous",
            "GL Date",
            "Ship Date",
            "CU",
            "Vendor",
            "Description",
            "Invoice/Doc",
            "Freight Amount",
            "Allocated Amount",
            "MFG Loc",
            "Ship Via",
            "Cubes",
            "Freight Per Cube",
            "Estimated Freight",
            "Actual vs Estimate"
        ]);

        for (const r of this.filteredRows) {
            rows.push([
            "Freight Match",
            this.statusLabel(r.period_status),
            r.parsed_order_no || "",
            r.order_history_match_method || "",
            r.order_history_count ?? "",
            r.order_history_ambiguous ? "YES" : "NO",
            this.fmtDate(r.gl_date || r.gl_date_yyyy_mm_dd),
            this.fmtDate(r.ship_date || r.ship_date_yyyy_mm_dd),
            r.cu || "",
            r.vendor_name || r.vendor_no || "",
            r.description || "",
            r.faktuurnr || r.docnumber || r.bkstnr || "",
            r.freight_amount ?? "",
            r.allocated_amount ?? "",
            r.mfg_loc || "",
            r.ship_via_cd || "",
            r.cubes ?? "",
            r.freight_per_cube ?? "",
            r.estimated_freight ?? "",
            r.freight_vs_estimate ?? ""
            ]);
        }

        if (this.includeMissingAccrual && this.missingLoaded) {
            for (const r of this.missingRows) {
            rows.push([
                "Missing Accrual",
                "Missing Freight",
                r.ord_no || "",
                r.freight_match_method || "",
                r.freight_match_count ?? "",
                r.freight_match_ambiguous ? "YES" : "NO",
                "",
                this.fmtDate(r.ship_date || r.ship_date_yyyy_mm_dd),
                this.cu,
                "",
                "",
                r.inv_no || "",
                "",
                "",
                r.mfg_loc || "",
                r.ship_via_cd_best || r.ship_via_cd || "",
                r.cubes ?? "",
                r.freight_per_cube ?? "",
                r.estimated_freight ?? "",
                ""
            ]);
            }
        }

        const csv = rows
            .map(r => r.map(val => `"${String(val ?? "").replace(/"/g, '""')}"`).join(","))
            .join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
            "download",
            `freight_analysis_${this.cu}_${this.startDate}_${this.endDate}.csv`
        );

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    },

    printFreight() {
      window.print();
    }
  }
};
</script>

<style scoped>
.freight-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

.main-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 90px;
}

.right-side-section {
  width: 100%;
  min-width: 0;
}

.sheet-scroll-container {
  max-height: 78vh;
  overflow-y: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
}

.sheet-scroll-container::-webkit-scrollbar {
  width: 10px;
}

.sheet-scroll-container::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}

.sheet-scroll-container::-webkit-scrollbar-track {
  background: #111;
}

.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input,
.select-input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.toggle-row {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.toggle {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 13px;
  color: #cbbfbf;
}

.toggle input {
  transform: translateY(1px);
}

.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 160px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

.warn-box {
  border-color: #888844;
  background: #1f1a0b;
  color: #fef3c7;
  width: 100%;
}

.warn-title {
  font-weight: bold;
  margin-bottom: 6px;
}

.big-number {
  font-size: 30px;
  font-weight: bold;
  color: #fff;
  line-height: 1;
  margin-bottom: 6px;
}

.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 120px;
  flex: 1 1 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 17px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
  word-break: break-word;
}

.tile-row {
  display: grid;
  grid-template-columns: repeat(6, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.metric-tile {
  border: 1px solid #888;
  border-radius: 10px;
  background: #0b0101;
  padding: 10px;
  text-align: center;
}

.metric-label {
  font-size: 13px;
  opacity: 0.85;
}

.metric-value {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
}

.dangerish {
  border-color: #aa4444;
  background: #220909;
}

.warningish {
  border-color: #888844;
  background: #1f1a0b;
}

.report-panel {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
  margin-bottom: 12px;
}

.hint-panel {
  margin-top: 20px;
  background-color: #0b0101;
}

.panel-title-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
}

.panel-title {
  font-size: 18px;
  font-weight: bold;
  color: #fff;
}

.table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

.freight-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1200px;
}

.freight-table th,
.freight-table td {
  border: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
}

.freight-table th {
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 3;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 4;
  background: #0b0101;
  color: #cbbfbf;
}

.order-col {
  min-width: 100px;
  font-weight: bold;
}

.desc-cell {
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.freight-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

.clickable-cell {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.status-chip {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid #777;
  font-size: 12px;
  background: #111;
  color: #eee;
}

.chip-ok {
  border-color: #4d7c4d;
  background: #102010;
  color: #d1fae5;
}

.chip-danger {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
}

.chip-warn {
  border-color: #888844;
  background: #1f1a0b;
  color: #fef3c7;
}

.row-danger td {
  background: rgba(90, 10, 10, 0.25);
}

.row-warn td {
  background: rgba(90, 80, 10, 0.22);
}

.row-ok td {
  background: rgba(10, 45, 20, 0.16);
}

.empty-row {
  padding: 14px;
  font-size: 14px;
  opacity: 0.8;
  text-align: center !important;
}

.small-muted {
  font-size: 12px;
  opacity: 0.85;
  color: #fff;
}

.missing-panel {
  margin-top: 12px;
}

.mini-wide {
  min-width: 900px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 14px;
}

.modal {
  width: min(1100px, 96vw);
  max-height: 92vh;
  overflow: auto;
  border-radius: 12px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.modal-title {
  font-size: 18px;
  font-weight: bold;
}

.modal-close {
  border: 1px solid #888;
  background: #111;
  color: #cbbfbf;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
}

.modal-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.kv-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #333;
  padding: 6px 0;
}

.kv-row span,
.kv-block span {
  opacity: 0.85;
}

.kv-row b {
  color: #fff;
  text-align: right;
}

.kv-block {
  margin-top: 8px;
}

.kv-block p {
  margin: 6px 0 0;
  color: #fff;
  white-space: normal;
}

.accrual-panel {
  margin-bottom: 12px;
}

.accrual-total-box {
  text-align: right;
  min-width: 180px;
}

.accrual-total {
  font-size: 26px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
}

.accrual-table {
  min-width: 1050px;
}

.accrual-detail-table {
  min-width: 1400px;
}

.current-period-row td {
  background: rgba(85, 149, 183, 0.28);
  color: #fff;
}

.money-small {
  font-size: 18px;
}

.accrual-tile-row {
  grid-template-columns: repeat(6, minmax(120px, 1fr));
}

@media (max-width: 1100px) {
  .accrual-tile-row {
    grid-template-columns: repeat(3, minmax(120px, 1fr));
  }
}

@media (max-width: 1100px) {
  .tile-row {
    grid-template-columns: repeat(3, minmax(120px, 1fr));
  }

  .modal-grid {
    grid-template-columns: 1fr;
  }
}

@media print {
  .left-side-section,
  .error-box,
  .warn-box,
  .modal-overlay {
    display: none !important;
  }

  .main-content {
    display: block !important;
    padding: 0 !important;
  }

  .sheet-scroll-container {
    max-height: none !important;
    overflow: visible !important;
    padding-right: 0 !important;
  }

  .jim-scheduling-container,
  .panel,
  .summary-bar,
  .metric-tile {
    background: white !important;
    color: black !important;
    border: none !important;
  }

  .freight-table th {
    background: #eee !important;
    color: black !important;
  }

  .sticky-col {
    background: white !important;
    color: black !important;
  }

  .freight-table td,
  .freight-table th {
    border: 1px solid #999 !important;
    color: black !important;
  }
}
</style>